const validateVINFormat = (vin) => /^[A-HJ-NPR-Z0-9]{17}$/.test(vin.trim());

const REQUIRED_FIELDS_FEES = [
  { key: "zipCode", label: "Zip Code" },
  { key: "state", label: "State" },
  { key: "city", label: "City" },
  { key: "country", label: "County" },
  { key: "basePrice", label: "Base Price" },
  { key: "plateType", label: "Plate Type" },
  { key: "transactionType", label: "Transaction Type" },
  { key: "titleStatus", label: "Title Status" },
  { key: "vinCode", label: "Vin Code" },
  { key: "vehicleType", label: "Vehicle Type" },
  { key: "fuelType", label: "Fuel Type" },
  { key: "vehicleWeight", label: "Vehicle Weight" },
  { key: "msrp", label: "MSRP" },
  { key: "modelYear", label: "Model Year" }
];

// Per the ATC AGF Consumption Guide ("Required Fields by State"), only
// District of Columbia and Oregon require <MPG> (i.e. City MPG). Highway
// and Combined MPG aren't listed as required by any state in the v4.0.1
// doc, but the table below is keyed so individual MPG fields can be
// flagged required for any state if ATC updates the spec or a state-level
// requirement is discovered later.
//
// Keys may be either the full state name or the 2-letter abbreviation —
// the ZIP decode returns full names in practice, but we normalize on
// both for safety. Values list the vehicleInfo field keys
// (`cityMpg` / `highwayMpg` / `combinedMpg`) that the state requires.
const STATE_REQUIRED_MPG_FIELDS = {
  "District of Columbia": ["cityMpg"],
  "DC":                   ["cityMpg"],
  "Oregon":               ["cityMpg"],
  "OR":                   ["cityMpg"],
};

const MPG_FIELD_LABELS = {
  cityMpg:     "MPG (City)",
  highwayMpg:  "Highway MPG",
  combinedMpg: "Combined MPG",
};

/**
 * Returns the set of MPG field keys (`cityMpg`, `highwayMpg`,
 * `combinedMpg`) required by the supplied state. Accepts either a full
 * state name ("Oregon") or an abbreviation ("OR"). Returns an empty array
 * when the state has no MPG requirements or none has been selected yet.
 */
export const getRequiredMpgFieldsForState = (stateValue) => {
  if (!stateValue) return [];
  const trimmed = String(stateValue).trim();
  return (
    STATE_REQUIRED_MPG_FIELDS[trimmed] ||
    STATE_REQUIRED_MPG_FIELDS[trimmed.toUpperCase()] ||
    []
  );
};

export { MPG_FIELD_LABELS };

const REQUIRED_FIELDS_DOCS = [
  { key: 'zipCode', label: 'Zip Code' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'country', label: 'County' },
  { key: 'vinCode', label: 'VIN' },
  { key: 'vehicleType', label: 'Vehicle Type' },
  { key: 'condition', label: 'Condition' },
  { key: 'transactionType', label: 'Transaction Type' },
  { key: 'transactionDate', label: 'Transaction Date' },
  { key: 'uploadDate', label: 'Upload Date' },
];

const FormValidation = (formType = '', formData = {}) => {
  const errors = {};

  switch (formType) {
    case "Title":
      const trimmedVin = formData.vinCode?.trim();

      if (!trimmedVin) {
        errors.vinCode = 'VIN is required';
      } else if (!validateVINFormat(trimmedVin)) {
        errors.vinCode = 'Please enter a valid 17-character VIN';
      }
      break;

    case "Fees":
      REQUIRED_FIELDS_FEES.forEach(({ key, label }) => {
        if (!formData[key]) {
          errors[key] = `${label} is required`;
        }
      });
      if (!(["Purchase", "Refinance"].includes(formData["transactionType"]))) {
        errors["transactionType"] = "Transaction Type must be either Purchase or Refinance";
      }
      // Conditional MPG validation: only flag the broken-out MPG fields
      // as required when the selected state lists them as required per
      // the ATC AGF Consumption Guide (or our local override map above).
      getRequiredMpgFieldsForState(formData.state).forEach((mpgKey) => {
        const value = formData[mpgKey];
        if (value === undefined || value === null || value === "") {
          errors[mpgKey] = `${MPG_FIELD_LABELS[mpgKey] || mpgKey} is required for ${formData.state}`;
        }
      });
      break;

    case "Docs":
      REQUIRED_FIELDS_DOCS.forEach(({ key, label }) => {
        if (!formData[key]) {
          errors[key] = `${label} is required`;
        }
      });
      break;

    default:
      return null;
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

export default FormValidation;
